import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { Product, Review, DoseType } from '../interfaces/product.interface';
import { collection, getDocs, doc, getDoc, query, where, addDoc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebase.config';
import { map, catchError, shareReplay, startWith } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ProductService {
  private productsSubject = new BehaviorSubject<Product[]>([]);
  // Share and replay the last value for all subscribers (optimized shared observable)
  public products$ = this.productsSubject.asObservable().pipe(shareReplay(1));

  private productsLoadingSubject = new BehaviorSubject<boolean>(true);
  public productsLoading$ = this.productsLoadingSubject.asObservable();

  private reviewsSubject = new BehaviorSubject<Review[]>([]);
  public reviews$ = this.reviewsSubject.asObservable();

  // Cache management
  private productsCache: Product[] | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache
  private isLoadingProducts = false;

  // Mock reviews data
  private mockReviews: Review[] = [
    {
      id: '1',
      userId: '1',
      productId: '1', // Add productId to match interface
      userName: 'Karolina Komel',
      userAvatar: 'https://placehold.co/50x50/4CAF50/FFFFFF?text=K',
      rating: 5,
      comment: 'I\'ve experienced no side effects with these pills, which is a huge plus. They help me relax without any unwanted drowsiness. Unlike other products I\'ve tried, these pills don\'t make me feel sluggish or foggy. They\'re perfect for unwinding in the evening.',
      date: new Date('2024-01-15'),
      verified: true
    }
  ];

  constructor() {
    // Check cache first and set loading state accordingly
    const now = Date.now();
    if (this.productsCache && (now - this.cacheTimestamp) < this.CACHE_DURATION) {
      // Cache is valid, load immediately without loading state
      this.productsSubject.next(this.productsCache);
      this.productsLoadingSubject.next(false);
    } else {
      // No cache, need to load - loading state is already true by default
      // Load products from database on service initialization (async, won't block)
      this.loadProductsFromDatabase();
    }
    // this.uploadMockProducts();
    this.reviewsSubject.next(this.mockReviews);
  }

  private mockProducts: Product[] = [
  {
    id: '1',
    name: 'Dose One',
    description: 'Foundation Growth Formula',
    price: 1999.00,
    originalPrice: 2499.00,
    image: [
            './product/Dose-1Boxdesign.svg',
            './product/Pouch1ForPowder.svg',
            './product/Pouch-1backsideforPowder.png',
          ],
    doseType: 'green',
    doseNumber: 1,
    features: [
      'Foundation growth formula',
      'Initiates height growth process',
      'Natural ingredients',
      '30 days supply'
    ],
    benefits: [
      'Activates growth hormones',
      'Strengthens bone density',
      'Improves calcium absorption',
      'No side effects',
      'Clinically tested formula'
    ],
    ingredients: [
      'L-Arginine 200mg',
      'Calcium Carbonate 150mg',
      'Vitamin D3 100mg',
      'Zinc Oxide 50mg'
    ],
    usage: [
      "Take 2 spoons of powder from both pots only in the morning, and mix with the chocolate-flavored powder. Mix this with milk or water. (Cow’s milk gives better results.)",
      "If taken when the body is warm (e.g., after practice/workout), it shows better overall effects.",
      "Avoid eating anything for at least half an hour after taking the powder."
    ],
    inStock: true,
    isActive: true,
    badge: 'DOSE 1',
    comboPricing: {
      twoDoses: 3499.00, // Green + Yellow
      threeDoses: 4999.00 // All three doses
    }
  },
  {
    id: '2',
    name: 'Dose Two',
    description: 'Growth Enhancement Formula',
    price: 2199.00,
    originalPrice: 2699.00,
    image:  [
            './product/./product/Dose-2Boxdesign.svg',
            './product/Pouch1ForPowder.svg',
            './product/Pouch-1backsideforPowder.png',
          ],
    doseType: 'yellow',
    doseNumber: 2,
    features: [
      'Advanced growth enhancement',
      'Maximizes growth potential',
      'Strengthens growth plates',
      '30 days supply'
    ],
    benefits: [
      'Accelerates height growth',
      'Enhances muscle development',
      'Improves posture',
      'Boosts metabolism',
      'Increases energy levels'
    ],
    ingredients: [
      'Growth Hormone Peptides 250mg',
      'L-Ornithine 200mg',
      'Magnesium 100mg',
      'Vitamin B Complex 75mg'
    ],
    usage: [
      "Take 2 spoons of powder from both pots only in the morning, and mix with the chocolate-flavored powder. Mix this with milk or water. (Cow’s milk gives better results.)",
      "If taken when the body is warm (e.g., after practice/workout), it shows better overall effects.",
      "Avoid eating anything for at least half an hour after taking the powder."
    ],
    inStock: true,
    isActive: true,
    badge: 'DOSE 2',
    comboPricing: {
      twoDoses: 3899.00, // Yellow + Red
      threeDoses: 4999.00 // All three doses
    }
  },
  {
    id: '3',
    name: 'Dose Three',
    description: 'Maximum Growth Formula',
    price: 2399.00,
    originalPrice: 2999.00,
    image:  [
            './product/Dose-3Boxdesign.svg',
            './product/Pouch1ForPowder.svg',
            './product/Pouch-1backsideforPowder.png',
          ],
    doseType: 'red',
    doseNumber: 3,
    features: [
      'Maximum growth potential',
      'Final growth consolidation',
      'Long-lasting results',
      '30 days supply'
    ],
    benefits: [
      'Maximizes final height gain',
      'Consolidates growth results',
      'Strengthens bone structure',
      'Improves overall physique',
      'Permanent height increase'
    ],
    ingredients: [
      'Advanced Growth Complex 300mg',
      'Collagen Peptides 200mg',
      'L-Lysine 150mg',
      'Phosphorus 100mg'
    ],
    usage: [
      "Take 2 spoons of powder from both pots only in the morning, and mix with the chocolate-flavored powder. Mix this with milk or water. (Cow’s milk gives better results.)",
      "If taken when the body is warm (e.g., after practice/workout), it shows better overall effects.",
      "Avoid eating anything for at least half an hour after taking the powder."
    ],
    inStock: true,
    isActive: true,
    badge: 'DOSE 3'
  }
];


  async uploadMockProducts(): Promise<void> {
    const productsRef = collection(db, 'coreProducts');

    try {
      for (const product of this.mockProducts) {
        const docRef = doc(productsRef, product.id); // Use custom ID
        await setDoc(docRef, product);
      }
      console.log('All products uploaded successfully!');
    } catch (error) {
      console.error('Error uploading products: ', error);
    }
  }

  private async loadProductsFromDatabase(): Promise<void> {
    // Prevent concurrent loads
    if (this.isLoadingProducts) {
      return;
    }

    // Return cached data if still valid (double check after constructor check)
    const now = Date.now();
    if (this.productsCache && (now - this.cacheTimestamp) < this.CACHE_DURATION) {
      // Cache is already loaded in constructor, skip
      return;
    }

    try {
      this.isLoadingProducts = true;
      this.productsLoadingSubject.next(true);
      
      // Use query with ordering for better performance
      const productsRef = collection(db, 'coreProducts');
      const querySnapshot = await getDocs(productsRef);
      
      const products: Product[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        products.push({
          id: doc.id,
          ...data
        } as Product);
      });
      
      // Sort products by doseNumber to maintain order
      products.sort((a, b) => (a.doseNumber || 0) - (b.doseNumber || 0));
      
      // Update cache
      this.productsCache = products;
      this.cacheTimestamp = now;
      
      this.productsSubject.next(products);
      this.productsLoadingSubject.next(false);
      this.isLoadingProducts = false;
    } catch (error) {
      console.error('Error loading products from database:', error);
      
      // Return cached data even if expired on error
      if (this.productsCache) {
        console.log('Error occurred, returning stale cache');
        this.productsSubject.next(this.productsCache);
      } else {
        this.productsSubject.next([]);
      }
      this.productsLoadingSubject.next(false);
      this.isLoadingProducts = false;
    }
  }

  // Add validation for dose types
  private validateProduct(product: Product): boolean {
    return !!(product && product.doseType && product.doseNumber);
  }

  // Update getProducts to filter invalid products (optimized with shareReplay)
  getProducts(): Observable<Product[]> {
    return this.products$.pipe(
      map(products => products.filter(p => this.validateProduct(p))),
      startWith([]), // Immediately emit empty array while loading
      catchError(error => {
        console.error('Error getting products:', error);
        return of([]);
      }),
      shareReplay(1) // Share the filtered result across all subscribers
    );
  }

  getProductById(id: string): Observable<Product | undefined> {
    return from(this.getProductByIdFromDb(id)).pipe(
      catchError(error => {
        console.error('Error fetching product by ID:', error);
        // Fallback to current loaded products
        const currentProducts = this.productsSubject.value;
        return of(currentProducts.find(p => p.id === id));
      })
    );
  }

  private async getProductByIdFromDb(id: string): Promise<Product | undefined> {
    try {
      const docRef = doc(db, 'coreProducts', id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const productData = docSnap.data();
        console.log('Product service - loaded from DB:', {
          id: docSnap.id,
          name: productData['name'],
          image: productData['image']
        });
        return {
          id: docSnap.id,
          ...productData
        } as Product;
      }
      return undefined;
    } catch (error) {
      console.error('Error fetching product by ID from database:', error);
      throw error;
    }
  }

  getProductsByDoseType(doseType: DoseType): Observable<Product[]> {
    return from(this.getProductsByDoseTypeFromDb(doseType)).pipe(
      catchError(error => {
        console.error('Error fetching products by dose type:', error);
        // Fallback to current loaded products
        const currentProducts = this.productsSubject.value;
        const filtered = currentProducts.filter(p => p.doseType === doseType);
        return of(filtered);
      })
    );
  }

  private async getProductsByDoseTypeFromDb(doseType: DoseType): Promise<Product[]> {
    try {
      const q = query(collection(db, 'coreProducts'), where('doseType', '==', doseType));
      const querySnapshot = await getDocs(q);
      const products: Product[] = [];
      
      querySnapshot.forEach((doc) => {
        products.push({
          id: doc.id,
          ...doc.data()
        } as Product);
      });
      
      return products;
    } catch (error) {
      console.error('Error fetching products by dose type from database:', error);
      throw error;
    }
  }

  getProductsByDoseNumber(doseNumber: number): Observable<Product[]> {
    return from(this.getProductsByDoseNumberFromDb(doseNumber)).pipe(
      catchError(error => {
        console.error('Error fetching products by dose number:', error);
        // Fallback to current loaded products
        const currentProducts = this.productsSubject.value;
        const filtered = currentProducts.filter(p => p.doseNumber === doseNumber);
        return of(filtered);
      })
    );
  }

  // Admin CRUD Operations
  async createProduct(productData: Omit<Product, 'id'>): Promise<string> {
    try {
      // Generate sequential ID
      const nextId = await this.getNextSequentialId();
      
      // Filter out undefined values and create product with sequential ID
      const cleanProductData = this.filterUndefinedValues(productData);
      const productWithId = { ...cleanProductData, id: nextId };
      await setDoc(doc(db, 'coreProducts', nextId), productWithId);
      
      console.log('Product created with sequential ID:', nextId);
      
      // Refresh products list (force cache refresh)
      await this.refreshProducts();
      
      return nextId;
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  }

  /**
   * Get the next sequential ID for products
   */
  private async getNextSequentialId(): Promise<string> {
    try {
      // Get all existing products to find the highest ID
      const products = await this.getAllProductsFromDb();
      
      if (products.length === 0) {
        return '1'; // First product
      }
      
      // Find the highest numeric ID
      const numericIds = products
        .map(p => p.id)
        .filter(id => !isNaN(Number(id)))
        .map(id => Number(id));
      
      if (numericIds.length === 0) {
        return '1'; // No numeric IDs found
      }
      
      const maxId = Math.max(...numericIds);
      const nextId = (maxId + 1).toString();
      
      console.log(`Next sequential ID: ${nextId} (max found: ${maxId})`);
      return nextId;
    } catch (error) {
      console.error('Error getting next sequential ID:', error);
      // Fallback to timestamp-based ID if sequential fails
      return Date.now().toString();
    }
  }

  /**
   * Get all products from database (helper method)
   */
  private async getAllProductsFromDb(): Promise<Product[]> {
    try {
      const querySnapshot = await getDocs(collection(db, 'coreProducts'));
      const products: Product[] = [];
      
      querySnapshot.forEach((doc) => {
        products.push({
          id: doc.id,
          ...doc.data()
        } as Product);
      });
      
      return products;
    } catch (error) {
      console.error('Error fetching all products from database:', error);
      return [];
    }
  }

  async updateProduct(productId: string, productData: Partial<Product>): Promise<void> {
    try {
      // Filter out undefined values to prevent Firebase errors
      const cleanProductData = this.filterUndefinedValues(productData);
      
      const docRef = doc(db, 'coreProducts', productId);
      await updateDoc(docRef, cleanProductData);
      console.log('Product updated successfully');
      
      // Refresh products list (force cache refresh)
      await this.refreshProducts();
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  }

  /**
   * Add a category to a product's categoryIds (keeps product–category in sync).
   */
  async addCategoryToProduct(productId: string, categoryId: string): Promise<void> {
    const product = await this.getProductByIdFromDb(productId);
    if (!product) return;
    const categoryIds = Array.isArray(product.categoryIds) ? product.categoryIds : (product.categoryIds ? [product.categoryIds] : []);
    if (categoryIds.includes(categoryId)) return;
    await this.updateProduct(productId, { categoryIds: [...categoryIds, categoryId] });
  }

  /**
   * Remove a category from a product's categoryIds.
   */
  async removeCategoryFromProduct(productId: string, categoryId: string): Promise<void> {
    const product = await this.getProductByIdFromDb(productId);
    if (!product) return;
    const categoryIds = (Array.isArray(product.categoryIds) ? product.categoryIds : (product.categoryIds ? [product.categoryIds] : [])).filter(id => id !== categoryId);
    await this.updateProduct(productId, { categoryIds });
  }

  /**
   * Remove undefined values from an object to prevent Firebase errors
   */
  private filterUndefinedValues(obj: any): any {
    if (obj === null || obj === undefined) {
      return {};
    }
    
    const filtered: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        if (Array.isArray(value)) {
          filtered[key] = value;
        } else if (typeof value === 'object' && value !== null && !(value instanceof Date)) {
          const filteredValue = this.filterUndefinedValues(value);
          if (Object.keys(filteredValue).length > 0) {
            filtered[key] = filteredValue;
          }
        } else {
          filtered[key] = value;
        }
      }
    }
    return filtered;
  }

  async deleteProduct(productId: string): Promise<void> {
    try {
      const docRef = doc(db, 'coreProducts', productId);
      await deleteDoc(docRef);
      console.log('Product deleted successfully');
      
      // Refresh products list (force cache refresh)
      await this.refreshProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  }

  async toggleProductStatus(productId: string, isActive: boolean): Promise<void> {
    try {
      const docRef = doc(db, 'coreProducts', productId);
      await updateDoc(docRef, { isActive });
      console.log('Product status toggled successfully');
      
      // Refresh products list (force cache refresh)
      await this.refreshProducts();
    } catch (error) {
      console.error('Error toggling product status:', error);
      throw error;
    }
  }

  async toggleProductStock(productId: string, inStock: boolean): Promise<void> {
    try {
      const docRef = doc(db, 'coreProducts', productId);
      await updateDoc(docRef, { inStock });
      console.log('Product stock toggled successfully');
      
      // Refresh products list (force cache refresh)
      await this.refreshProducts();
    } catch (error) {
      console.error('Error toggling product stock:', error);
      throw error;
    }
  }

  private async getProductsByDoseNumberFromDb(doseNumber: number): Promise<Product[]> {
    try {
      const q = query(collection(db, 'coreProducts'), where('doseNumber', '==', doseNumber));
      const querySnapshot = await getDocs(q);
      const products: Product[] = [];
      
      querySnapshot.forEach((doc) => {
        products.push({
          id: doc.id,
          ...doc.data()
        } as Product);
      });
      
      return products;
    } catch (error) {
      console.error('Error fetching products by dose number from database:', error);
      throw error;
    }
  }

  getReviewsForProduct(productId: string): Observable<Review[]> {
    const filtered = this.mockReviews.filter(r => r.productId === productId);
    return new BehaviorSubject(filtered).asObservable();
  }

  // Get combo pricing for multiple doses
  getComboPricing(doseNumbers: number[]): number {
    if (doseNumbers.length === 2) {
      // Two doses combo
      return 3699.00; // Average of combo prices
    } else if (doseNumbers.length === 3) {
      // All three doses
      return 4999.00;
    }
    return 0;
  }

  // Calculate discount for combo purchases
  calculateComboDiscount(doseNumbers: number[]): number {
    const currentProducts = this.productsSubject.value;
    const individualTotal = doseNumbers.reduce((total, doseNum) => {
      const product = currentProducts.find(p => p.doseNumber === doseNum);
      // Price is now optional - use 0 as fallback
      return total + (product?.price ?? 0);
    }, 0);

    const comboPrice = this.getComboPricing(doseNumbers);
    return individualTotal - comboPrice;
  }

  // Add method to refresh products from database
  async refreshProducts(): Promise<void> {
    // Clear cache to force fresh load
    this.productsCache = null;
    this.cacheTimestamp = 0;
    await this.loadProductsFromDatabase();
  }

  // Clear cache manually (useful for admin updates)
  clearCache(): void {
    this.productsCache = null;
    this.cacheTimestamp = 0;
  }
}
